import { EntityViewFilter } from '../../../../entities/entities/views/entity.view.filter';

/** An entity-defined segment filter: a named filter over the entity's records, rendered as a
 *  dashboard tile (dot + label + count). Declared on entity metadata — view-independent. */
export interface MetadataEntitySegmentFilter<T = any> {
    _id: string;
    label: string;
    color?: string;
    /** Selected on load when no segment is chosen. First one wins; absent on all = the no-filter segment. */
    isDefault?: boolean;
    /** Empty/absent = the "All" segment (no extra filter). Reuses the standard view-filter format. */
    filters?: EntityViewFilter<T>[];
}

/** How segment filters are rendered. Discriminated by `type` so each style carries its own config;
 *  add a new style as another union member (non-breaking). Absent = tiles with default config. */
export interface MetadataEntitySegmentFilterTilesDisplay {
    type: 'tiles';
    /** Show the count stat on each tile. Default true. */
    showCount?: boolean;
}

/** Compact single-line pills (dot + label + inline count) instead of stacked tiles. Sizes to
 *  content with no min-width floor, so the row absorbs a larger `items` count and narrow boards
 *  (the board is squeezed by view settings in push mode) before it has to scroll. */
export interface MetadataEntitySegmentFilterPillsDisplay {
    type: 'pills';
    /** Show the count beside each label. Default true. */
    showCount?: boolean;
}

export type MetadataEntitySegmentFilterDisplay = MetadataEntitySegmentFilterTilesDisplay | MetadataEntitySegmentFilterPillsDisplay;

export type MetadataEntitySegmentFilterDisplayType = 'tiles' | 'pills';

/** An entity's segment filter feature config: the segment bundles + how they render. */
export interface MetadataEntitySegmentFilterConfig<T = any> {
    items: MetadataEntitySegmentFilter<T>[];
    /** How segments render + per-style config. Absent = tiles with defaults. */
    display?: MetadataEntitySegmentFilterDisplay;
    /** `items` requires every items response to carry complete, segment-independent `segmentCounts`; absent keeps the separate count query. */
    countsSource?: 'items';
}

/** Single source for the `display` semantics: should per-segment counts be requested/shown.
 *  Shared by the board's count fetch gate and the tiles component's count render, so a new
 *  display member is interpreted in one place, not duplicated per consumer. Both current styles
 *  render counts — a future countless style must be excluded here or it will fetch needlessly. */
export const segmentFilterCountsEnabled = (config?: MetadataEntitySegmentFilterConfig): boolean =>
    !!config?.items?.length
    && (config.display?.showCount ?? true);
